import {useCallback, useEffect, useRef, useState} from 'react';

import {fetchAccountDomains} from 'src/openapi/admin/apis/SsoApi';
import {useAppDispatch} from 'src/store/hooks';

import type {DomainEntry} from '../../../utils/domain-validation';

export function useAccountDomains() {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchDomains = useCallback(async (): Promise<DomainEntry[]> => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        try {
            const response = await dispatch(
                fetchAccountDomains({signal: controller.signal}),
            );

            if (controller.signal.aborted) {
                return [];
            }

            if (response.payload?.success) {
                const domains = response.payload.data?.domains ?? [];
                return domains.map(domain => ({value: domain, verified: true, warning: null}));
            }

            return [];
        } catch {
            return [];
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [dispatch]);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    return {fetchDomains, loading};
}
